/**
 * 1.5.13 §1c.5 — is a newer version of the app live? The build writes /version.json (vite.config.ts: the release
 * from package.json and a build id); the running bundle carries the same two values. The page asks for the file
 * every few minutes and on every navigation (a burst of navigations is one request), and when the answer differs
 * from what is running the banner says "A new version is ready — Reload" (components/PwaUpdate.tsx). Reloading is
 * the person's press. A missing file, a failed request or an answer that is not the file (dev, tests, offline, the
 * sign-in page) is simply "nothing new" — never an error.
 *
 * 1.6.5 §2: the watch keeps what the site answered — the target version, so the banner can say "Updating to
 * 1.6.5…" and Settings "App 1.6.4 · the site has 1.6.5" — goes on asking after it knows (a second deploy moves the
 * target), and may be asked with a shorter gap when the app comes to the front. What happens then (the page
 * reloads by itself when nothing would be lost) is lib/app-update.ts.
 */
// The Vite defines (vite.config.ts), declared here as well so this file stands on its own wherever it is compiled
// (the build-config test imports it); outside a build they do not exist, hence the typeof checks below.
declare const __APP_VERSION__: string | undefined;
declare const __BUILD_TIME__: string | undefined;
declare const __APP_RELEASE__: string | undefined;

export interface VersionFile {
  version: string;
  build: string;
}

export const VERSION_URL = '/version.json';
/** How often an open tab asks by itself. */
export const VERSION_POLL_MS = 5 * 60 * 1000;
/** Requests are at least this far apart, however many navigations there are. */
export const VERSION_MIN_GAP_MS = 30 * 1000;
/** 1.6.5: the gap when the app comes to the front (visible, pageshow, focus arrive together: one request). */
export const VERSION_FRONT_GAP_MS = 3 * 1000;

/** The build id as vite.config.ts writes it into /version.json: the commit (7) and the build time. */
export const buildIdOf = (sha: string, builtAt: string): string => `${sha.slice(0, 7)}-${builtAt}`;

/** What this bundle is (the Vite defines; outside a build — unit tests — a fixed "dev"). */
export function runningVersion(): VersionFile {
  const version = typeof __APP_RELEASE__ === 'string' ? __APP_RELEASE__ : '0.0.0';
  const build = typeof __APP_VERSION__ === 'string' && typeof __BUILD_TIME__ === 'string' ? buildIdOf(__APP_VERSION__, __BUILD_TIME__) : 'dev';
  return { version, build };
}

/** The file as it is live now, or null for anything else (never throws). */
export async function fetchVersion(fetcher: typeof fetch = fetch, now: number = Date.now()): Promise<VersionFile | null> {
  try {
    const res = await fetcher(`${VERSION_URL}?t=${now}`, { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    if (!(res.headers.get('content-type') ?? '').includes('json')) return null; // the app shell answered instead of the file
    const body = (await res.json()) as Partial<VersionFile> | null;
    if (!body || typeof body.version !== 'string' || typeof body.build !== 'string' || !body.version || !body.build) return null;
    return { version: body.version, build: body.build };
  } catch {
    return null;
  }
}

export interface VersionWatch {
  /** What this page is running. */
  readonly running: VersionFile;
  /** Is a newer version live (from /version.json, or the service worker said so)? */
  isStale(): boolean;
  subscribe(listener: () => void): () => void;
  /** 1.6.5: what the site last answered (null until it has), whether newer or the same. */
  latest(): VersionFile | null;
  /** 1.6.5: the version to update to — the site's answer when it is not what is running; null when the worker said so first. */
  target(): VersionFile | null;
  /** Asks /version.json unless it was asked less than the gap ago (or is being asked right now). */
  check(o?: { minGapMs?: number }): Promise<void>;
  /** The service worker has the new version in place. */
  markStale(): void;
}

export function createVersionWatch(o: { running?: VersionFile; fetcher?: typeof fetch; now?: () => number; minGapMs?: number } = {}): VersionWatch {
  const running = o.running ?? runningVersion();
  const now = o.now ?? (() => Date.now());
  const gap = o.minGapMs ?? VERSION_MIN_GAP_MS;
  const listeners = new Set<() => void>();
  let stale = false;
  let latest: VersionFile | null = null;
  let lastAsked = -Infinity;
  let asking: Promise<void> | null = null;
  const tell = () => listeners.forEach((l) => l());
  const setStale = () => {
    if (stale) return;
    stale = true;
    tell();
  };
  const differs = (file: VersionFile) => file.build !== running.build || file.version !== running.version;
  return {
    running,
    isStale: () => stale,
    latest: () => latest,
    target: () => (latest && differs(latest) ? latest : null),
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    check(c = {}) {
      if (asking) return asking;
      if (now() - lastAsked < (c.minGapMs ?? gap)) return Promise.resolve();
      lastAsked = now();
      asking = fetchVersion(o.fetcher ?? fetch, lastAsked)
        .then((file) => {
          if (!file) return;
          // the same object while the answer is the same (a store snapshot)
          const moved = !latest || latest.build !== file.build || latest.version !== file.version;
          if (moved) latest = file;
          if (differs(file) && !stale) setStale();
          else if (moved) tell();
        })
        .finally(() => {
          asking = null;
        });
      return asking;
    },
    markStale: setStale,
  };
}

/** The app's one watch. */
export const versionWatch: VersionWatch = createVersionWatch();
